import { useEffect, useState } from 'react';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/16x16/delete-trash.svg';
import {
  Dialog,
  FlexColumn,
  TextField,
} from '@elementary/common-ui/components';
import { AccountEnvironment } from '@elementary/common-ui/types';
import { Divider, Typography } from '@mui/material';
import { fontWeight } from '@elementary/common-ui/style/utils';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  environment?: AccountEnvironment;
  canDelete?: boolean;
};

function DeleteEnvironmentDialog({
  open,
  onClose,
  environment,
  onConfirm,
  loading,
  canDelete = false,
}: Props) {
  const envName = environment?.name;
  const [confirmationName, setConfirmationName] = useState('');

  useEffect(() => {
    if (open) {
      setConfirmationName('');
    }
  }, [open, envName]);

  const normalizedEnvName = envName?.trim() ?? '';
  const isNameMatch =
    normalizedEnvName.length > 0 &&
    confirmationName.trim().toLowerCase() === normalizedEnvName.toLowerCase();
  const isConfirmDisabled = !canDelete || !isNameMatch || !!loading;

  return (
    <Dialog
      open={open}
      titleIcon={<TrashIcon />}
      title="Delete environment"
      message={
        <Typography sx={{ fontWeight: fontWeight('semibold') }}>
          Are you sure you want to delete{' '}
          <Typography
            component="span"
            sx={{ fontWeight: fontWeight('semibold') }}
          >
            {envName ?? 'this environment'}
          </Typography>
          ?
        </Typography>
      }
      secondaryMessage={
        <FlexColumn sx={{ gap: 1 }}>
          <Typography>This will permanently delete:</Typography>
          <Typography component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
            <li>Environment&apos;s cloud test results and history</li>
            <li>Environment&apos;s incidents history</li>
            <li>Environment&apos;s integrations and permissions</li>
          </Typography>
          <Typography
            sx={{ color: 'status.error.main', fontWeight: fontWeight('bold') }}
          >
            This action cannot be undone.
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Typography sx={{ fontWeight: fontWeight('semibold') }}>
            Type the environment name to confirm:
          </Typography>
          <TextField
            placeholder={envName ? `Type "${envName}"` : undefined}
            value={confirmationName}
            onChange={(event) => setConfirmationName(event.target.value)}
            disabled={!canDelete || !!loading}
            fullWidth
            size="large"
          />
        </FlexColumn>
      }
      onClose={onClose}
      onConfirmButtonClick={onConfirm}
      isLoading={!!loading}
      confirmText={loading ? 'Deleting...' : 'Delete environment'}
      confirmDisabled={isConfirmDisabled}
      variant="error"
    />
  );
}

export default DeleteEnvironmentDialog;
